<template>
  <div
    class="radio-wrap"
    @keydown.enter.prevent="onChange"
  >
    <label :class="{ 'disabled': disabled }">
      <input
        type="radio"
        :name="name"
        :value="value"
        :checked="isChecked"
        :disabled="disabled"
        @change="onChange"
      >
      <span v-dompurify-html="label" />
    </label>
  </div>
</template>

<script>
  /**
   * Exported events
   */
  const events = {
    CHANGE: 'change'
  }

  export default {
    props: {
      name: {
        type: String,
        required: false,
        default: ''
      },
      label: {
        type: String,
        required: true
      },
      isChecked: {
        type: Boolean,
        default: true
      },
      value: {
        type: String,
        required: true
      },
      disabled: {
        type: Boolean,
        default: false
      }
    },
    methods: {
      onChange (event) {
        this.$emit(events.CHANGE, event)
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import '~assets/styles/common.scss';

  .radio-wrap {

    label {
      display: block;

      &:not(.disabled) {
        cursor: pointer;
      }

      input[type="radio"] {
        display: none;
      }
    }
  }

  .radio-wrap.block {
    float: left;
    width: 50%;

    &:first-child {

      span {
        border-right: none;
        border-radius: 4px 0 0 4px;
      }
    }

    &:last-child {

      span {
        border-radius: 0 4px 4px 0;

        &:after {
          display: none;
        }
      }
    }

    label {
      text-align: center;
      font-weight: 500;
      color: $blue-medium;

      input[type="radio"] {

        &:checked + span {
          position: relative;
          background-color: $blue-white;
          border-color: $blue-medium;

          &:after {
            content: '';
            position: absolute;
            right: -1px;
            top: 0;
            width: 1px;
            height: 100%;
            background-color: $blue-medium;
          }
        }
      }

      span {
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 0 16px;
        height: 48px;
        border: 1px solid $formInputBorder;

        @include bp-mf-tablet {
          height: 52px;
        }

        &:hover {
          border-color: $blue-medium;
        }
      }
    }
  }

  .radio-wrap.circle {
    display: inline-block;
    vertical-align: top;
    margin-bottom: 10px;

    span {
      position: relative;
      display: block;
      line-height: 24px;
      font-size: em(14);
      color: $blue-dark;

      &:before {
        display: inline-block;
        content: '';
        height: 22px;
        width: 22px;
        border-radius: 50%;
        background-color: white;
        vertical-align: top;
        margin-right: 5px;
        border: 1px solid $blue-medium;
      }
    }

    input[type="radio"] {

      &:checked + span {
        color: $blue-medium;

        &:before {
          @include icon-font-thin();
          border: none;
          content: $icon-radio;
          font-size: em(24);
          height: 23px;
          width: 23px;
        }
      }
    }
  }

  .radio-wrap.no-label {
    width: 100%;
    height: 100%;

    label {
      text-align: center;
      width: 100%;
      height: 100%;
    }

    input[type="radio"] {

      &:checked + span {
        position: relative;
        background-color: $blue-medium;

        &:before {
          content: $line-icon-checkmark;
          @include line-icon-font();
          position: absolute;
          top: 0;
          right: 0;
          left: 0;
          font-size: em(11);
          font-weight: 700;
          line-height: 24px;
          color: #ffffff;
        }
      }

      &:disabled + span {
        border: 1px solid #ccc;
      }
    }

    span {
      position: relative;
      top: calc(50% - 12px);
      display: inline-block;
      width: 24px;
      height: 24px;
      background-color: #ffffff;
      border: 1px solid $blue-medium;
      border-radius: 50%;
      text-align: center;
    }
  }

  .radio-wrap.blue-circle {
    display: inline-block;
    text-align: center;

    &:not(:last-child) {
      margin-right: 24px;
    }

    input[type="radio"] {

      &:checked + span {
        position: relative;

        &:after {
          content: '';
          display: block;
          position: absolute;
          top: 4px;
          left: 8px;
          width: 3px;
          height: 7px;
          border: solid #ffffff;
          border-width: 0 2px 2px 0;
          -webkit-transform: rotate(45deg);
          -ms-transform: rotate(45deg);
          transform: rotate(45deg);
        }
      }
    }

    span {
      position: relative;
      display: block;
      padding-left: 26px;
      line-height: 20px;
      font-size: em(14);
      font-weight: 500;
      color: $blue-gray;

      &:before {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        height: 20px;
        width: 20px;
        border-radius: 50%;
        background-color: $blue-gray;
      }
    }
  }

  .error {

    .radio-wrap {

      label {

        span {
          border-color: $red-dark!important;
        }
      }
    }
  }

</style>
